import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Chip,
    Alert,
    CircularProgress,
    Stack,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemDecorator,
    ListItemContent,
} from "@mui/joy";
import { Save, RotateCcw, CheckCircle, AlertCircle, FlaskConical, Wrench, Link2 } from "lucide-react";
import { useFormulas, useLaborFormulas, useProductDependencies } from "../../../../hooks/query";

// ─── Types ────────────────────────────────────────────────────────────────────

type TabKey = "material" | "labor" | "product-mapping";

const TABS: { key: TabKey; label: string; icon: React.ElementType; description: string }[] = [
    {
        key: "material",
        label: "Material Formulas",
        icon: FlaskConical,
        description: "Quantity formulas for shingles, flashing, gutters and other materials.",
    },
    {
        key: "labor",
        label: "Labor Formulas",
        icon: Wrench,
        description: "Quantity formulas used to calculate labor task hours and units.",
    },
    {
        key: "product-mapping",
        label: "Product Mapping",
        icon: Link2,
        description:
            "Shingle-system bundles: which companion products (starter, hip & ridge, nails, etc.) map to each field shingle by manufacturer product ID.",
    },
];

// ─── Variable reference data ─────────────────────────────────────────────────

const VARIABLE_GROUPS = [
    {
        label: "Roof Dimensions",
        vars: [
            { name: "Area", note: "Total roof area (sq ft)" },
            { name: "Eave", note: "Eave length (linear ft)" },
            { name: "Rake", note: "Rake length (linear ft)" },
            { name: "Ridge", note: "Ridge length (linear ft)" },
            { name: "Hip", note: "Hip length (linear ft)" },
            { name: "Valley", note: "Valley length (linear ft)" },
            { name: "Headwall", note: "Head wall flashing (linear ft)" },
            { name: "Step", note: "Step flashing (linear ft)" },
            { name: "RollMetal", note: "W/roll valley metal (linear ft)" },
            { name: "CounterFlashing", note: "Counter flashing (linear ft)" },
        ],
    },
    {
        label: "Penetrations",
        vars: [
            { name: "RoofVent", note: "Roof vent count" },
            { name: "Chimney", note: "Chimney count" },
            { name: "PipeJack", note: "Plumbing pipe jack count" },
            { name: "SplitBoots", note: "Split boot count" },
            { name: "FurnaceVents", note: "Furnace vent count" },
            { name: "BathroomVents", note: "Bathroom vent count" },
            { name: "DryerVents", note: "Dryer vent count" },
            { name: "Cooler", note: "Evaporative cooler count" },
        ],
    },
    {
        label: "Skylights",
        vars: [
            { name: "Skylight", note: "Skylight count" },
            { name: "Cricket", note: "Cricket build count" },
        ],
    },
    {
        label: "Gutters",
        vars: [
            { name: "GutterLength", note: "Gutter run length (linear ft)" },
            { name: "DownspoutLength", note: "Downspout length (linear ft)" },
        ],
    },
    {
        label: "Other",
        vars: [{ name: "SolarPanels", note: "Solar panel count" }],
    },
    {
        label: "Computed (material only)",
        vars: [
            { name: "FieldShingles", note: "Result of Field Shingles formula" },
            { name: "FieldShingles_Selection", note: "Selected shingle product name" },
        ],
    },
];

// ─── Variable Reference Panel ─────────────────────────────────────────────────

function VariableReference() {
    return (
        <Box
            sx={{
                width: 230,
                flexShrink: 0,
                borderLeft: "1px solid",
                borderColor: "divider",
                overflowY: "auto",
                pt: 2,
                pb: 3,
            }}
        >
            <Typography
                level="body-xs"
                sx={{
                    px: 2,
                    pb: 1,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: "text.tertiary",
                    display: "block",
                }}
            >
                Variables
            </Typography>

            {VARIABLE_GROUPS.map((group) => (
                <Box key={group.label} mb={2}>
                    <Typography
                        level="body-xs"
                        sx={{
                            px: 2,
                            py: 0.5,
                            fontWeight: 600,
                            color: "text.secondary",
                            display: "block",
                        }}
                    >
                        {group.label}
                    </Typography>
                    <Box px={1.5} display="flex" flexDirection="column" gap={0.25}>
                        {group.vars.map((v) => (
                            <Box
                                key={v.name}
                                sx={{
                                    px: 1,
                                    py: 0.6,
                                    borderRadius: 6,
                                    cursor: "default",
                                    "&:hover": { bgcolor: "neutral.softBg" },
                                }}
                            >
                                <Typography
                                    level="body-xs"
                                    sx={{
                                        fontFamily: "'Fira Code', monospace",
                                        fontWeight: 500,
                                        color: "primary.plainColor",
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {v.name}
                                </Typography>
                                <Typography
                                    level="body-xs"
                                    sx={{ color: "text.tertiary", fontSize: "10.5px", lineHeight: 1.3 }}
                                >
                                    {v.note}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

interface JsonEditorProps {
    value: object;
    onChange: (val: object) => void;
    onError: (err: string | null) => void;
}

function JsonTextarea({ value, onChange, onError }: JsonEditorProps) {
    const [raw, setRaw] = useState(() => JSON.stringify(value, null, 2));
    const [parseError, setParseError] = useState<string | null>(null);

    useEffect(() => {
        setRaw(JSON.stringify(value, null, 2));
        setParseError(null);
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const text = e.target.value;
        setRaw(text);
        try {
            const parsed = JSON.parse(text);
            setParseError(null);
            onError(null);
            onChange(parsed);
        } catch (err: any) {
            const msg = err?.message || "Invalid JSON";
            setParseError(msg);
            onError(msg);
        }
    };

    return (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5, overflow: "hidden" }}>
            <textarea
                value={raw}
                onChange={handleChange}
                spellCheck={false}
                style={{
                    flex: 1,
                    width: "100%",
                    height: "100%",
                    fontFamily: "'Fira Code', 'JetBrains Mono', 'Courier New', monospace",
                    fontVariantLigatures: "contextual",

                    fontSize: "12.5px",
                    lineHeight: 1.65,
                    padding: "14px 16px",
                    border: parseError
                        ? "1.5px solid var(--joy-palette-danger-400)"
                        : "1.5px solid var(--joy-palette-divider)",
                    borderRadius: "10px",
                    background: "var(--joy-palette-background-level1)",
                    color: "var(--joy-palette-text-primary)",
                    resize: "none",
                    outline: "none",
                    boxSizing: "border-box",
                    overflowY: "auto",
                    transition: "border-color 0.15s",
                }}
            />
            {parseError && (
                <Typography level="body-xs" sx={{ color: "danger.400", flexShrink: 0 }}>
                    ⚠ {parseError}
                </Typography>
            )}
        </Box>
    );
}

// ─── Right Panel (editor area for one formula type) ───────────────────────────

interface EditorPanelProps {
    label: string;
    description: string;
    useList: () => { data: any; isLoading: boolean };
    useSave: () => { mutateAsync: (data: any) => Promise<any>; isPending: boolean };
}

function EditorPanel({ label, description, useList, useSave }: EditorPanelProps) {
    const { data, isLoading } = useList();
    const { mutateAsync, isPending } = useSave();

    const [edited, setEdited] = useState<object | null>(null);
    const [jsonError, setJsonError] = useState<string | null>(null);
    const [saveStatus, setSaveStatus] = useState<"idle" | "success" | "error">("idle");

    useEffect(() => {
        if (data && edited === null) setEdited(data);
    }, [data]);

    const handleReset = () => {
        if (data) {
            setEdited({ ...data });
            setJsonError(null);
            setSaveStatus("idle");
        }
    };

    const handleSave = async () => {
        if (!edited || jsonError) return;
        try {
            await mutateAsync(edited);
            setSaveStatus("success");
            setTimeout(() => setSaveStatus("idle"), 3000);
        } catch {
            setSaveStatus("error");
            setTimeout(() => setSaveStatus("idle"), 4000);
        }
    };

    if (isLoading || !data) {
        return (
            <Box flex={1} display="flex" justifyContent="center" alignItems="center">
                <CircularProgress size="md" />
            </Box>
        );
    }

    const isDirty = JSON.stringify(edited) !== JSON.stringify(data);
    const formulaCount = Array.isArray((data as any)?.rules)
        ? (data as any).rules.length
        : Object.keys(data).length;

    return (
        // Outer: fills the right panel completely, clips overflow
        <Box flex={1} display="flex" flexDirection="column" overflow="hidden">
            {/* ── Fixed header area ── */}
            <Box
                px={3}
                pt={2.5}
                pb={2}
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                flexWrap="wrap"
                gap={1}
                flexShrink={0}
            >
                <Box>
                    <Typography level="title-md" sx={{ fontWeight: 700 }}>
                        {label}
                    </Typography>
                    <Typography level="body-sm" sx={{ color: "text.tertiary", mt: 0.25 }}>
                        {description}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.5 }}>
                        {formulaCount} {label.toLowerCase().includes("mapping") ? "rule" : "formula"}
                        {formulaCount !== 1 ? "s" : ""} defined · Override file:{" "}
                        <code style={{ fontSize: 11 }}>
                            {label.toLowerCase().includes("mapping")
                                ? "product-dependencies-override.json"
                                : "src/assets/*-override.json"}
                        </code>
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center">
                    {isDirty && (
                        <Chip size="sm" color="warning" variant="soft">
                            Unsaved changes
                        </Chip>
                    )}
                    <Button
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        startDecorator={<RotateCcw size={13} />}
                        onClick={handleReset}
                        disabled={!isDirty}
                    >
                        Reset
                    </Button>
                    <Button
                        size="sm"
                        color="primary"
                        startDecorator={isPending ? <CircularProgress size="sm" /> : <Save size={13} />}
                        onClick={handleSave}
                        disabled={!!jsonError || isPending || !isDirty}
                        loading={isPending}
                    >
                        Save
                    </Button>
                </Stack>
            </Box>

            <Divider />

            {/* ── Status banners (only shown when needed, no reserved space) ── */}
            {saveStatus === "success" && (
                <Box px={3} pt={1.5} flexShrink={0}>
                    <Alert color="success" variant="soft" startDecorator={<CheckCircle size={15} />} size="sm">
                        Saved — backend override file updated successfully.
                    </Alert>
                </Box>
            )}
            {saveStatus === "error" && (
                <Box px={3} pt={1.5} flexShrink={0}>
                    <Alert color="danger" variant="soft" startDecorator={<AlertCircle size={15} />} size="sm">
                        Save failed. Check the backend console for details.
                    </Alert>
                </Box>
            )}

            {/* ── Editor + Reference side-by-side ── */}
            {edited !== null && (
                <Box flex={1} display="flex" overflow="hidden">
                    {/* JSON textarea */}
                    <Box flex={1} pt={2} px={3} pb={3} display="flex" flexDirection="column" overflow="hidden">
                        <JsonTextarea value={edited} onChange={setEdited} onError={setJsonError} />
                    </Box>

                    {/* Variable reference */}
                    <VariableReference />
                </Box>
            )}
        </Box>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function FormulaEditor() {
    const [activeTab, setActiveTab] = useState<TabKey>("material");
    const formulasHook = useFormulas();
    const laborFormulasHook = useLaborFormulas();
    const productDependenciesHook = useProductDependencies();

    const activePanel = TABS.find((t) => t.key === activeTab)!;

    return (
        <Box flex={1} display="flex" flexDirection="column" height="100%">
            {/* Page header — matches Breadcrumb pattern */}
            <Box px={3} py={2} display="flex" alignItems="center" gap={1.5}>
                <FlaskConical size={18} />
                <Box>
                    <Typography level="title-sm" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        Formula Editor
                    </Typography>
                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                        Administration · Platform
                    </Typography>
                </Box>
            </Box>

            <Divider />

            {/* Two-column layout */}
            <Box flex={1} display="flex" overflow="hidden">
                <Box
                    sx={{
                        width: 220,
                        flexShrink: 0,
                        borderRight: "1px solid",
                        borderColor: "divider",
                        pt: 2,
                    }}
                >
                    <Typography
                        level="body-xs"
                        sx={{
                            px: 2,
                            pb: 1,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                            color: "text.tertiary",
                            display: "block",
                        }}
                    >
                        Sections
                    </Typography>
                    <List size="sm" sx={{ gap: 0, p: 0, overflow: "hidden" }}>
                        {TABS.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.key;
                            return (
                                <ListItem key={tab.key} sx={{ p: 0 }}>
                                    <ListItemButton
                                        selected={isActive}
                                        onClick={() => setActiveTab(tab.key)}
                                        sx={{
                                            borderRadius: 0,
                                            px: 2,
                                            py: 1.25,
                                            gap: 1.25,
                                            position: "relative",
                                            // Active left-edge accent bar
                                            "&::before": isActive
                                                ? {
                                                      content: '""',
                                                      position: "absolute",
                                                      left: 0,
                                                      top: 0,
                                                      bottom: 0,
                                                      width: "3px",
                                                      borderRadius: "0 2px 2px 0",
                                                      bgcolor: "primary.500",
                                                  }
                                                : {},
                                            "&.Mui-selected": {
                                                bgcolor: "primary.softBg",
                                                color: "primary.plainColor",
                                            },
                                            "&:hover:not(.Mui-selected)": {
                                                bgcolor: "neutral.softBg",
                                            },
                                        }}
                                    >
                                        <ListItemDecorator sx={{ minWidth: 20, color: "inherit" }}>
                                            <Icon size={15} />
                                        </ListItemDecorator>
                                        <ListItemContent>
                                            <Typography
                                                level="body-sm"
                                                sx={{ fontWeight: isActive ? 600 : 400, color: "inherit" }}
                                            >
                                                {tab.label}
                                            </Typography>
                                        </ListItemContent>
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                </Box>

                {/* Right content — JSON editor */}
                <Box flex={1} overflow="auto" display="flex" flexDirection="column">
                    {activeTab === "material" && (
                        <EditorPanel
                            key="material"
                            label={activePanel.label}
                            description={activePanel.description}
                            useList={formulasHook.useList}
                            useSave={formulasHook.useSave}
                        />
                    )}
                    {activeTab === "labor" && (
                        <EditorPanel
                            key="labor"
                            label={activePanel.label}
                            description={activePanel.description}
                            useList={laborFormulasHook.useList}
                            useSave={laborFormulasHook.useSave}
                        />
                    )}
                    {activeTab === "product-mapping" && (
                        <EditorPanel
                            key="product-mapping"
                            label={activePanel.label}
                            description={activePanel.description}
                            useList={productDependenciesHook.useList}
                            useSave={productDependenciesHook.useSave}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    );
}

export default FormulaEditor;
